import { useContext, useState } from 'react';
import { GlobalContext, PROD_TYPES, LevelConfig, LevelsConfig } from '../../context/GlobalContext';
import { calculate_user_financing_options } from '../../hooks/firebase';
import {
	Smartphone,
	Tablet,
	Laptop,
	Package,
	DollarSign,
	AlertCircle,
	CheckCircle,
	CreditCard,
	ChevronLeft,
	ChevronRight,
	Edit,
	Save,
	X,
	Plus,
	Trash2
} from 'lucide-react';
import Logo from '../../components/Logo';
import Input from '../../components/Input';
import { format_currency } from '../../utils/formatters';
import firestore from '../../firestore';
import { Dialog } from '../../components/Dialog';

// Map product types to icons
const productIcons = {
	phone: Smartphone,
	iphone: Smartphone,
	tablet: Tablet,
	laptop: Laptop,
	other: Package,
	loan: DollarSign
};

const sanitizeProductTypes = (productTypes: unknown) => {
	if (!Array.isArray(productTypes)) return [];
	const allowed = new Set<string>(PROD_TYPES as unknown as string[]);

	return productTypes
		.filter((p): p is string => typeof p === 'string')
		.map((p) => p.trim())
		.filter((p) => p.length > 0)
		.filter((p) => allowed.has(p));
};

// Format currency
const formatCurrency = (amount: number | undefined, currency_code: string | undefined, no_decimals = true) => {
	if (amount === undefined || currency_code === undefined) return 'N/A';
	return `${currency_code}${format_currency(amount, no_decimals)}`;
};

// UI utility classes (no logic changes)
const focusRing =
	'focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2 focus-visible:ring-offset-neutral-100 dark:focus-visible:ring-offset-neutral-950';
const btnBase = `inline-flex select-none items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium transition-colors disabled:pointer-events-none disabled:opacity-50 ${focusRing}`;
const iconBtnBase = `inline-flex items-center justify-center rounded-lg p-2 transition-colors ${focusRing}`;

const creditLineLabel: Record<'primary' | 'secondary', string> = {
	primary: 'Primaria',
	secondary: 'Secundaria'
};

type EditableData = {
	benefits: string[];
	recarga_fee: string;
	credit_line: string;
	credit_line_secondary: string;
	personal_loan_min: string;
	personal_loan_max: string;
	personal_loan_required_points: string;
	product_configs: {
		[key: string]: {
			price_min: string;
			price_max: string;
			down_payment_min: string;
			down_payment_max: string;
			payment_options: Array<{ installments: string; factor: string }>;
		};
	};
};

type EditableBaseData = {
	base_price_ranges: {
		[key: string]: {
			min: string;
			max: string;
			credit_line?: '' | 'primary' | 'secondary';
		};
	};
	base_financing_options: Array<{
		product_types: string[];
		down_payment_min: string;
		down_payment_max: string;
		payment_options: Array<{ installments: string; factor: string }>;
	}>;
};

type PriceRange = {
	min: number;
	max: number;
	credit_line?: 'primary' | 'secondary';
};

type PaymentOption = {
	installments: number;
	factor: number;
};

type FinancingOption = {
	product_types: Array<(typeof PROD_TYPES)[number]>;
	down_payment: {
		min: number;
		max: number;
		granularity: number;
	};
	payment_options: PaymentOption[];
};

export default function FinancingOptionsConfig() {
	const { LevelsConfig, internalCountry, CountrySettings } = useContext(GlobalContext);
	const country = (internalCountry?.get() || 'venezuela') as keyof typeof LevelsConfig;
	const currency_code = CountrySettings?.currency_code;
	const personalLoanRequiredPoints = CountrySettings?.services_config?.personal_loans?.required_points;
	const [currentLevelIndex, setCurrentLevelIndex] = useState(0);
	const [editMode, setEditMode] = useState(false);
	const [saving, setSaving] = useState(false);
	const [editableData, setEditableData] = useState<EditableData | null>(null);
	const [editableBaseData, setEditableBaseData] = useState<EditableBaseData | null>(null);
	const [newBenefit, setNewBenefit] = useState('');
	const [dialogMessage, setDialogMessage] = useState<string | null>(null);
	const [viewMode, setViewMode] = useState<'levels' | 'base'>('levels');

	if (!LevelsConfig || !country) return null;

	const levels = [
		{ id: 'level_1', data: calculate_user_financing_options(LevelsConfig, country, 'level_1') },
		{ id: 'level_2', data: calculate_user_financing_options(LevelsConfig, country, 'level_2') },
		{ id: 'level_3', data: calculate_user_financing_options(LevelsConfig, country, 'level_3') },
		{ id: 'level_4', data: calculate_user_financing_options(LevelsConfig, country, 'level_4') }
	];

	const currentLevel = levels[currentLevelIndex];
	const { id, data } = currentLevel;

	if (!data) return null;

	const { level_information, recarga_fee, credit_line, credit_line_secondary, personal_loan } = data;

	const handlePrevious = () => {
		if (currentLevelIndex > 0) {
			setCurrentLevelIndex(currentLevelIndex - 1);
		}
	};

	const handleNext = () => {
		if (currentLevelIndex < levels.length - 1) {
			setCurrentLevelIndex(currentLevelIndex + 1);
		}
	};

	const isPreviousDisabled = currentLevelIndex === 0;
	const isNextDisabled = currentLevelIndex === levels.length - 1;

	// Initialize editable data from current level
	const initializeEditableData = () => {
		if (!data) return;

		const productConfigs: EditableData['product_configs'] = {};
		PROD_TYPES.forEach((productType) => {
			const productData = data[productType];
			if (productData) {
				productConfigs[productType] = {
					price_min: String(productData.price_range?.min || 0),
					price_max: String(productData.price_range?.max || 0),
					down_payment_min: String(productData.down_payment?.min || 0),
					down_payment_max: String(productData.down_payment?.max || 0),
					payment_options: (productData.payment_options || []).map((opt) => ({
						installments: String(opt.installments || 0),
						factor: String(((opt.factor - 1) * 100).toFixed(2) || 0)
					}))
				};
			}
		});

		setEditableData({
			benefits: [...(level_information?.benefits || [])],
			recarga_fee: String(recarga_fee || 0),
			credit_line: String(credit_line || 0),
			credit_line_secondary: String(credit_line_secondary || 0),
			personal_loan_min: String(personal_loan?.min || 0),
			personal_loan_max: String(personal_loan?.max || 0),
			personal_loan_required_points: String(personalLoanRequiredPoints || 0),
			product_configs: productConfigs
		});
	};

	const handleEnterEditMode = () => {
		initializeEditableData();
		setEditMode(true);
	};

	const handleCancelEdit = () => {
		setEditMode(false);
		setEditableData(null);
		setEditableBaseData(null);
		setNewBenefit('');
	};

	// Initialize base editable data
	const initializeBaseEditableData = () => {
		const countryConfig: LevelsConfig = LevelsConfig[country];
		if (!countryConfig) return;

		const basePriceRanges: EditableBaseData['base_price_ranges'] = {};
		PROD_TYPES.forEach((productType) => {
			const priceRange = countryConfig.base_price_ranges?.[productType];
			if (priceRange) {
				basePriceRanges[productType] = {
					min: String(priceRange.min || 0),
					max: String(priceRange.max || 0),
					credit_line: priceRange.credit_line ?? ''
				};
			}
		});

		const baseFinancingOptions: EditableBaseData['base_financing_options'] =
			countryConfig.base_financing_options
				?.map((opt: any) => ({
					product_types: sanitizeProductTypes(opt.product_types).filter((p) => p !== 'loan'),
					down_payment_min: String(opt.down_payment?.min || 0),
					down_payment_max: String(opt.down_payment?.max || 0),
					payment_options: (opt.payment_options || []).map((payOpt: any) => ({
						installments: String(payOpt.installments || 0),
						factor: String(((payOpt.factor - 1) * 100).toFixed(2) || 0)
					}))
				}))
				.filter((opt) => opt.product_types.length > 0) || [];

		setEditableBaseData({
			base_price_ranges: basePriceRanges,
			base_financing_options: baseFinancingOptions
		});
	};

	const handleEnterBaseEditMode = () => {
		initializeBaseEditableData();
		setEditMode(true);
	};

	const handleSaveBase = async () => {
		if (!editableBaseData || !LevelsConfig) return;

		setSaving(true);
		try {
			const countryConfig = LevelsConfig[country];
			if (!countryConfig) return;

			// Build updated base price ranges
			const updatedBasePriceRanges: Record<string, PriceRange> = {};
			PROD_TYPES.forEach((productType) => {
				const config = editableBaseData.base_price_ranges[productType];
				if (config) {
					const min = parseFloat(config.min);
					const max = parseFloat(config.max);
					const creditLine =
						config.credit_line === 'primary' || config.credit_line === 'secondary' ? config.credit_line : undefined;
					if (!isNaN(min) && !isNaN(max)) {
						updatedBasePriceRanges[productType] = {
							min,
							max,
							...(creditLine ? { credit_line: creditLine } : {})
						};
					}
				}
			});

			// Build updated base financing options
			const updatedBaseFinancingOptions = editableBaseData.base_financing_options
				.map((opt) => ({
					...opt,
					product_types: sanitizeProductTypes(opt.product_types).filter((p) => p !== 'loan')
				}))
				.filter((opt) => opt.product_types.length > 0 && opt.payment_options.length > 0)
				.map((opt) => {
					const downMin = parseFloat(opt.down_payment_min);
					const downMax = parseFloat(opt.down_payment_max);
					const paymentOptions: PaymentOption[] = opt.payment_options
						.filter((payOpt) => {
							const inst = parseFloat(payOpt.installments);
							const fact = parseFloat(payOpt.factor);
							return !isNaN(inst) && inst > 0 && !isNaN(fact) && fact >= 0;
						})
						.map((payOpt) => {
							const factor = 1 + parseFloat(payOpt.factor) / 100;
							return {
								installments: parseFloat(payOpt.installments),
								factor: Math.round(factor * 1000000) / 1000000
							};
						});

					// Sort payment options by installments (ascending)
					paymentOptions.sort((a, b) => a.installments - b.installments);

					return {
						product_types: opt.product_types as Array<(typeof PROD_TYPES)[number]>,
						down_payment: {
							min: !isNaN(downMin) ? downMin : 40,
							max: !isNaN(downMax) ? downMax : 50,
							granularity: 5
						},
						payment_options: paymentOptions
					};
				});

			// Update Firestore
			await firestore
				.collection('Levels')
				.doc(country)
				.update({
					base_price_ranges: updatedBasePriceRanges,
					base_financing_options: updatedBaseFinancingOptions as unknown as LevelsConfig['base_financing_options']
				});

			setEditMode(false);
			setEditableBaseData(null);
		} catch (error) {
			console.error('Error saving base config:', error);
			setDialogMessage('Error al guardar la configuración base');
		} finally {
			setSaving(false);
		}
	};

	const handleSave = async () => {
		if (!editableData || !LevelsConfig) return;

		setSaving(true);
		try {
			const levelId = id as 'level_1' | 'level_2' | 'level_3' | 'level_4';
			const countryConfig: LevelsConfig = LevelsConfig[country];

			if (!countryConfig) return;

			const currentLevelConfig: LevelConfig | undefined = countryConfig[levelId];

			if (!currentLevelConfig) return;

			// Build updated level config
			const updatedLevel: LevelConfig = {
				...currentLevelConfig,
				information: {
					...currentLevelConfig.information,
					benefits: editableData.benefits
				}
			};

			// Update financial fields
			const recargaFee = parseFloat(editableData.recarga_fee);
			if (!isNaN(recargaFee) && recargaFee > 0) {
				updatedLevel.recarga_fee = recargaFee;
			}

			const creditLine = parseFloat(editableData.credit_line);
			if (!isNaN(creditLine) && creditLine >= 0) {
				updatedLevel.credit_line = creditLine;
			}

			const creditLineSecondary = parseFloat(editableData.credit_line_secondary);
			if (!isNaN(creditLineSecondary) && creditLineSecondary >= 0) {
				updatedLevel.credit_line_secondary = creditLineSecondary;
			}

			// Update personal loan
			const loanMin = parseFloat(editableData.personal_loan_min);
			const loanMax = parseFloat(editableData.personal_loan_max);
			const loanRequiredPoints = parseFloat(editableData.personal_loan_required_points);

			// Only include personal_loan if at least one value is > 0
			if ((!isNaN(loanMin) && loanMin > 0) || (!isNaN(loanMax) && loanMax > 0)) {
				updatedLevel.personal_loan = {
					...(!isNaN(loanMin) && loanMin >= 0 && { min: loanMin }),
					...(!isNaN(loanMax) && loanMax >= 0 && { max: loanMax })
				};
			} else {
				delete updatedLevel.personal_loan;
			}

			// Build efficient additional_financing_options and additional_price_ranges
			const newPriceRanges: Record<string, PriceRange> = {};
			const financingOptionsMap = new Map<string, FinancingOption>();

			PROD_TYPES.forEach((productType) => {
				const config = editableData.product_configs[productType];
				if (!config) return;

				// Price ranges
				const priceMin = parseFloat(config.price_min);
				const priceMax = parseFloat(config.price_max);
				const basePriceRange = countryConfig.base_price_ranges?.[productType];

				if (!basePriceRange) return;

				// Only save if different from base
				if ((!isNaN(priceMin) && priceMin !== basePriceRange.min) || (!isNaN(priceMax) && priceMax !== basePriceRange.max)) {
					newPriceRanges[productType] = {
						min: !isNaN(priceMin) ? priceMin : basePriceRange.min,
						max: !isNaN(priceMax) ? priceMax : basePriceRange.max
					};
				}

				// Financing options
				const downMin = parseFloat(config.down_payment_min);
				const downMax = parseFloat(config.down_payment_max);
				const allPaymentOptions: PaymentOption[] = config.payment_options
					.filter((opt) => {
						const inst = parseFloat(opt.installments);
						const fact = parseFloat(opt.factor);
						return !isNaN(inst) && inst > 0 && !isNaN(fact) && fact > 0;
					})
					.map((opt) => {
						const factor = 1 + parseFloat(opt.factor) / 100;
						// Round to 6 decimal places to avoid floating point precision issues
						return {
							installments: parseFloat(opt.installments),
							factor: Math.round(factor * 1000000) / 1000000
						};
					});

				// Check if this config differs from base
				const baseFinancing = countryConfig.base_financing_options?.find((opt) => opt.product_types?.includes(productType));

				// Check down payment differences
				const downPaymentDifferent =
					(!isNaN(downMin) && downMin !== (baseFinancing?.down_payment?.min || 40)) ||
					(!isNaN(downMax) && downMax !== (baseFinancing?.down_payment?.max || 50));

				// Filter payment options to only include those that differ from base
				const paymentOptions: PaymentOption[] = allPaymentOptions.filter((opt) => {
					const baseOption = baseFinancing?.payment_options?.find((baseOpt) => baseOpt.installments === opt.installments);
					if (!baseOption || !baseOption.factor) return true; // New installment option not in base
					// Check if factor is different (with small tolerance for floating point)
					return Math.abs(opt.factor - baseOption.factor) > 0.000001;
				});

				const isDifferentFromBase = downPaymentDifferent || paymentOptions.length > 0;

				if (isDifferentFromBase) {
					const key = JSON.stringify({
						down_min: downMin,
						down_max: downMax,
						options: paymentOptions
					});

					if (!financingOptionsMap.has(key)) {
						const financingOption: FinancingOption = {
							product_types: [productType],
							down_payment: {
								min: !isNaN(downMin) ? downMin : baseFinancing?.down_payment?.min || 40,
								max: !isNaN(downMax) ? downMax : baseFinancing?.down_payment?.max || 50,
								granularity: baseFinancing?.down_payment?.granularity || 5
							},
							payment_options: paymentOptions
						};

						// Only include down_payment if it's actually different from base
						if (!downPaymentDifferent) {
							delete (financingOption as any).down_payment;
						}

						financingOptionsMap.set(key, financingOption);
					} else {
						financingOptionsMap.get(key)!.product_types.push(productType);
					}
				}
			});

			// Only set if there are differences
			if (Object.keys(newPriceRanges).length > 0) {
				updatedLevel.additional_price_ranges = newPriceRanges as LevelConfig['additional_price_ranges'];
			} else {
				delete updatedLevel.additional_price_ranges;
			}

			if (financingOptionsMap.size > 0) {
				updatedLevel.additional_financing_options = Array.from(
					financingOptionsMap.values()
				) as unknown as LevelConfig['additional_financing_options'];
			} else {
				delete updatedLevel.additional_financing_options;
			}

			const batch = firestore.batch();
			const levelsRef = firestore.collection('Levels').doc(country);
			const countrySettingsRef = firestore.collection('CountrySettings').doc(country);

			batch.update(levelsRef, {
				[levelId]: updatedLevel
			});

			if (!isNaN(loanRequiredPoints) && loanRequiredPoints >= 0) {
				batch.update(countrySettingsRef, {
					'services_config.personal_loans.required_points': loanRequiredPoints
				});
			}

			await batch.commit();

			setEditMode(false);
			setEditableData(null);
		} catch (error) {
			console.error('Error building level config:', error);
			setDialogMessage('Error al construir la configuración');
		} finally {
			setSaving(false);
		}
	};

	return (
		<div className="mx-auto w-full max-w-7xl space-y-8 p-6">
			{/* View Mode Toggle */}
			<div className="flex justify-center">
				<div
					role="tablist"
					aria-label="Modo de vista"
					className="inline-flex gap-1 rounded-xl bg-neutral-900 p-1 shadow-inner ring-1 ring-inset ring-neutral-800"
				>
					<button
						role="tab"
						id="tab-levels"
						aria-controls="panel-levels"
						aria-selected={viewMode === 'levels'}
						onClick={() => {
							if (editMode) {
								setDialogMessage('Debes guardar o cancelar los cambios antes de cambiar de vista');
								return;
							}
							setViewMode('levels');
						}}
						className={`${btnBase} px-5 py-1.5 ${
							viewMode === 'levels'
								? 'bg-primary-600 text-white shadow-sm hover:bg-primary-600'
								: 'bg-transparent text-neutral-300 hover:text-white'
						}`}
						aria-label="Ver niveles"
					>
						Ver Niveles
					</button>
					<button
						role="tab"
						id="tab-base"
						aria-controls="panel-base"
						aria-selected={viewMode === 'base'}
						onClick={() => {
							if (editMode) {
								setDialogMessage('Debes guardar o cancelar los cambios antes de cambiar de vista');
								return;
							}
							setViewMode('base');
						}}
						className={`${btnBase} px-5 py-1.5 ${
							viewMode === 'base'
								? 'bg-primary-600 text-white shadow-sm hover:bg-primary-600'
								: 'bg-transparent text-neutral-300 hover:text-white'
						}`}
						aria-label="Ver configuración base"
					>
						Configuración Base
					</button>
				</div>
			</div>

			{viewMode === 'levels' ? (
				<div id="panel-levels" role="tabpanel" aria-labelledby="tab-levels">
					{/* Navigation Header */}
					<div className="mb-8 space-y-4">
						<div className="flex items-center justify-between gap-4">
							{/* Previous Button */}
							<button
								onClick={handlePrevious}
								disabled={isPreviousDisabled || editMode}
								aria-label="Anterior nivel"
								aria-disabled={isPreviousDisabled || editMode}
								className={`${iconBtnBase} bg-neutral-900 text-neutral-0 hover:bg-neutral-800 ${
									isPreviousDisabled || editMode ? 'pointer-events-none !opacity-30' : ''
								}`}
							>
								<ChevronLeft className="h-6 w-6" />
							</button>

							{/* Level Badge - Centered */}
							<div className="flex select-none items-center gap-3 rounded-xl bg-neutral-0/80 px-6 py-3 shadow-sm ring-1 ring-inset ring-neutral-200 backdrop-blur-sm dark:bg-neutral-1000/60 dark:ring-neutral-800">
								<Logo
									size="min"
									className="h-8 w-8"
									fill={level_information?.color ? `fill-[${level_information.color}]` : 'fill-primary-500'}
									style={{ fill: level_information?.color }}
								/>
								<div className="flex flex-col">
									<span className="text-lg font-semibold text-neutral-1000 dark:text-neutral-0">
										{level_information?.name || id.replace('_', ' ').toUpperCase()}
									</span>
									{level_information?.points_range && (
										<span className="text-sm text-neutral-700 dark:text-neutral-400">
											{level_information.points_range.min}
											{level_information.points_range.max ? `-${level_information.points_range.max}` : '+'} puntos
										</span>
									)}
								</div>
							</div>

							{/* Next Button */}
							<button
								onClick={handleNext}
								disabled={isNextDisabled || editMode}
								aria-label="Siguiente nivel"
								aria-disabled={isNextDisabled || editMode}
								className={`${iconBtnBase} bg-neutral-900 text-neutral-0 hover:bg-neutral-800 ${
									isNextDisabled || editMode ? 'pointer-events-none !opacity-30' : ''
								}`}
							>
								<ChevronRight className="h-6 w-6" />
							</button>
						</div>

						{/* Edit Controls - Below on mobile, inline on desktop */}
						<div className="flex justify-center gap-2">
							{!editMode ? (
								<button
									onClick={handleEnterEditMode}
									className={`${btnBase} bg-primary-600 text-white hover:bg-primary-700`}
									aria-label="Editar nivel"
								>
									<Edit className="h-4 w-4" />
									Editar
								</button>
							) : (
								<>
									<button
										onClick={handleCancelEdit}
										disabled={saving}
										className={`${btnBase} bg-neutral-700 text-white hover:bg-neutral-600`}
										aria-label="Cancelar edición"
									>
										<X className="h-4 w-4" />
										Cancelar
									</button>
									<button
										onClick={handleSave}
										disabled={saving}
										className={`${btnBase} bg-green-600 text-white hover:bg-green-700`}
										aria-label="Guardar cambios"
									>
										<Save className="h-4 w-4" />
										{saving ? 'Guardando...' : 'Guardar'}
									</button>
								</>
							)}
						</div>
					</div>

					{/* Level Content */}
					<div className="overflow-hidden rounded-xl bg-foreground/70 shadow-sm ring-1 ring-inset ring-neutral-800 dark:bg-neutral-950/50">
						{/* Level Details */}
						<div className="border-b border-neutral-800 p-6 dark:border-neutral-800">
							{/* Benefits and Financial Info Container */}
							<div className="grid gap-6 lg:grid-cols-2">
								{/* Benefits */}
								<div>
									<h3 className="mb-2 text-sm font-semibold text-neutral-200 dark:text-neutral-300">Beneficios:</h3>
									{!editMode ? (
										<div className="flex flex-col gap-2">
											{level_information?.benefits && level_information.benefits.length > 0 ? (
												level_information.benefits.map((benefit, idx) => (
													<div key={idx} className="flex items-center gap-1 text-sm text-neutral-300 dark:text-neutral-400">
														<CheckCircle className="h-4 w-4 text-primary-500" />
														<span>{benefit}</span>
													</div>
												))
											) : (
												<span className="text-sm italic text-neutral-500">No hay beneficios configurados</span>
											)}
										</div>
									) : (
										<div className="space-y-2">
											{editableData?.benefits.map((benefit, idx) => (
												<div key={idx} className="flex w-full items-center gap-2">
													<Input
														placeholder="Beneficio (mínimo 5 caracteres)"
														value={benefit}
														onChange={(value) => {
															if (!editableData) return;
															const newBenefits = [...editableData.benefits];
															newBenefits[idx] = value;
															setEditableData({ ...editableData, benefits: newBenefits });
														}}
														containerClassName="flex-1"
														clearable={false}
													/>
													<button
														onClick={() => {
															if (!editableData) return;
															const newBenefits = editableData.benefits.filter((_, i) => i !== idx);
															setEditableData({ ...editableData, benefits: newBenefits });
														}}
														className={`${iconBtnBase} shrink-0 bg-red-600 text-white hover:bg-red-700`}
														aria-label="Eliminar beneficio"
													>
														<Trash2 className="h-4 w-4" />
													</button>
												</div>
											))}
											<div className="flex w-full items-center gap-2">
												<Input
													placeholder="Nuevo beneficio"
													value={newBenefit}
													onChange={setNewBenefit}
													containerClassName="flex-1"
													clearable={false}
												/>
												<button
													onClick={() => {
														if (!editableData || newBenefit.trim().length < 5) return;
														setEditableData({
															...editableData,
															benefits: [...editableData.benefits, newBenefit.trim()]
														});
														setNewBenefit('');
													}}
													disabled={newBenefit.trim().length < 5}
													className={`${iconBtnBase} shrink-0 bg-green-600 text-white hover:bg-green-700 disabled:opacity-50`}
													aria-label="Agregar beneficio"
												>
													<Plus className="h-4 w-4" />
												</button>
											</div>
										</div>
									)}
								</div>

								{/* Financial Info */}
								<div className="space-y-4 lg:pl-6">
									{/* Recarga Fee */}
									<div className="flex min-h-16 w-full items-center rounded-lg bg-neutral-900/70 p-4 shadow-sm ring-1 ring-inset ring-neutral-800 dark:bg-neutral-950">
										<div className="flex w-full items-center justify-between gap-4">
											<h3 className="shrink-0 text-sm font-semibold text-neutral-200 dark:text-neutral-300">Tarifa de recarga</h3>
											{!editMode ? (
												recarga_fee !== undefined ? (
													<span className="text-lg font-bold text-primary-600 dark:text-primary-400">
														{formatCurrency(recarga_fee, currency_code, false)}
													</span>
												) : (
													<div className="flex items-center gap-2 text-amber-600 dark:text-amber-400">
														<AlertCircle className="h-4 w-4" />
														<span className="text-sm">No configurado</span>
													</div>
												)
											) : (
												<div className="w-32">
													<Input
														placeholder="0.00"
														value={editableData?.recarga_fee || ''}
														onChange={(value) => {
															if (!editableData) return;
															setEditableData({ ...editableData, recarga_fee: value });
														}}
														allowedChars="numeric"
														prefix={currency_code}
														clearable={false}
													/>
												</div>
											)}
										</div>
									</div>

									{/* Credit Line */}
									<div className="flex min-h-16 w-full items-center rounded-lg bg-neutral-900/70 p-4 shadow-sm ring-1 ring-inset ring-neutral-800 dark:bg-neutral-950">
										<div className="flex w-full items-center justify-between gap-4">
											<h3 className="shrink-0 text-sm font-semibold text-neutral-200 dark:text-neutral-300">Línea de crédito</h3>
											{!editMode ? (
												credit_line !== undefined ? (
													<span className="text-lg font-bold text-primary-600 dark:text-primary-400">
														{formatCurrency(credit_line, currency_code)}
													</span>
												) : (
													<div className="flex items-center gap-2 text-yellow-500 dark:text-amber-400">
														<AlertCircle className="h-4 w-4" />
														<span className="text-sm">No configurado</span>
													</div>
												)
											) : (
												<div className="w-32">
													<Input
														placeholder="0"
														value={editableData?.credit_line || ''}
														onChange={(value) => {
															if (!editableData) return;
															setEditableData({ ...editableData, credit_line: value });
														}}
														allowedChars="numbers_only"
														prefix={currency_code}
														clearable={false}
													/>
												</div>
											)}
										</div>
									</div>

									{/* Credit Line Secondary */}
									<div className="flex min-h-16 w-full items-center rounded-lg bg-neutral-900/70 p-4 shadow-sm ring-1 ring-inset ring-neutral-800 dark:bg-neutral-950">
										<div className="flex w-full items-center justify-between gap-4">
											<h3 className="shrink-0 text-sm font-semibold text-neutral-200 dark:text-neutral-300">
												Línea de crédito secundaria
											</h3>
											{!editMode ? (
												credit_line_secondary !== undefined ? (
													<span className="text-lg font-bold text-primary-600 dark:text-primary-400">
														{formatCurrency(credit_line_secondary, currency_code)}
													</span>
												) : (
													<div className="flex items-center gap-2 text-yellow-500 dark:text-amber-400">
														<AlertCircle className="h-4 w-4" />
														<span className="text-sm">No configurado</span>
													</div>
												)
											) : (
												<div className="w-32">
													<Input
														placeholder="0"
														value={editableData?.credit_line_secondary || ''}
														onChange={(value) => {
															if (!editableData) return;
															setEditableData({ ...editableData, credit_line_secondary: value });
														}}
														allowedChars="numbers_only"
														prefix={currency_code}
														clearable={false}
													/>
												</div>
											)}
										</div>
									</div>
								</div>
							</div>
						</div>

						{/* Personal Loan Section */}
						<div className="border-b border-neutral-800 p-6 dark:border-neutral-800">
							<div className="mb-4 flex items-center gap-2">
								<DollarSign className="h-5 w-5 text-primary-500" />
								<h3 className="text-base font-semibold text-neutral-200 dark:text-neutral-300">Préstamo Personal</h3>
							</div>

							<div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
								{/* Loan Range Min */}
								<div className="flex flex-col gap-2 rounded-lg bg-neutral-950/50 p-4 shadow-sm ring-1 ring-inset ring-neutral-800 dark:ring-neutral-700">
									<span className="text-xs font-medium text-neutral-400">Monto mínimo</span>
									{!editMode ? (
										personal_loan?.min !== undefined ? (
											<span className="text-xl font-bold text-primary-600 dark:text-primary-400">
												{formatCurrency(personal_loan.min, currency_code)}
											</span>
										) : (
											<div className="flex items-center gap-2 text-amber-600 dark:text-amber-400">
												<AlertCircle className="h-4 w-4" />
												<span className="text-sm font-medium">No configurado</span>
											</div>
										)
									) : (
										<Input
											placeholder="0"
											value={editableData?.personal_loan_min || ''}
											onChange={(value) => {
												if (!editableData) return;
												setEditableData({ ...editableData, personal_loan_min: value });
											}}
											allowedChars="numbers_only"
											prefix={currency_code}
											clearable={false}
										/>
									)}
								</div>

								{/* Loan Range Max */}
								<div className="flex flex-col gap-2 rounded-lg bg-neutral-950/50 p-4 shadow-sm ring-1 ring-inset ring-neutral-800 dark:ring-neutral-700">
									<span className="text-xs font-medium text-neutral-400">Monto máximo</span>
									{!editMode ? (
										personal_loan?.max !== undefined ? (
											<span className="text-xl font-bold text-primary-600 dark:text-primary-400">
												{formatCurrency(personal_loan.max, currency_code)}
											</span>
										) : (
											<div className="flex items-center gap-2 text-amber-600 dark:text-amber-400">
												<AlertCircle className="h-4 w-4" />
												<span className="text-sm font-medium">No configurado</span>
											</div>
										)
									) : (
										<Input
											placeholder="0"
											value={editableData?.personal_loan_max || ''}
											onChange={(value) => {
												if (!editableData) return;
												setEditableData({ ...editableData, personal_loan_max: value });
											}}
											allowedChars="numbers_only"
											prefix={currency_code}
											clearable={false}
										/>
									)}
								</div>

								{/* Country Personal Loan Points Requirement */}
								<div className="flex flex-col gap-2 rounded-lg bg-neutral-950/50 p-4 shadow-sm ring-1 ring-inset ring-neutral-800 dark:ring-neutral-700">
									<span className="text-xs font-medium text-neutral-400">Puntos mínimos requeridos en el país</span>
									{!editMode ? (
										personalLoanRequiredPoints !== undefined ? (
											<span className="text-xl font-bold text-primary-600 dark:text-primary-400">
												{personalLoanRequiredPoints}
											</span>
										) : (
											<div className="flex items-center gap-2 text-amber-600 dark:text-amber-400">
												<AlertCircle className="h-4 w-4" />
												<span className="text-sm font-medium">No configurado</span>
											</div>
										)
									) : (
										<Input
											placeholder="0"
											value={editableData?.personal_loan_required_points || ''}
											onChange={(value) => {
												if (!editableData) return;
												setEditableData({ ...editableData, personal_loan_required_points: value });
											}}
											allowedChars="numbers_only"
											clearable={false}
										/>
									)}
								</div>
							</div>
						</div>

						{/* Product Types */}
						<div className="p-6">
							<div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
								{(['loan', ...PROD_TYPES.filter((p) => p !== 'loan')] as Array<(typeof PROD_TYPES)[number]>).map(
									(productType) => {
										const Icon = productIcons[productType];
										const productData = data[productType];
										const { down_payment, payment_options, price_range } = productData || {};
										const editConfig = editableData?.product_configs[productType];

										return (
											<div
												key={productType}
												aria-disabled={!editMode && (!payment_options || payment_options.length === 0)}
												className={`group rounded-xl p-4 shadow-sm ring-1 ring-inset transition-all ${
													!payment_options || payment_options.length === 0
														? 'bg-neutral-900/50 opacity-60 ring-neutral-800 dark:bg-neutral-900/30 dark:ring-neutral-800'
														: 'bg-neutral-1000 ring-neutral-800 focus-within:ring-2 focus-within:ring-primary-500 hover:-translate-y-0.5 hover:shadow-md hover:ring-neutral-700 dark:bg-background dark:ring-neutral-800'
												}`}
											>
												{/* Product Header */}
												<div className="mb-3 flex items-center gap-2">
													<Icon
														className={`h-5 w-5 ${
															!payment_options || payment_options.length === 0 ? 'text-neutral-500' : 'text-primary-500'
														}`}
													/>
													<h4
														className={`font-semibold capitalize ${
															!payment_options || payment_options.length === 0
																? 'text-neutral-500 dark:text-neutral-500'
																: 'text-neutral-100 dark:text-neutral-200'
														}`}
													>
														{productType === 'loan' ? 'Préstamo Personal' : productType}
													</h4>
													{(!payment_options || payment_options.length === 0) && !editMode && (
														<span className="ml-auto rounded-full bg-neutral-800/60 px-2 py-0.5 text-[11px] text-neutral-300 ring-1 ring-inset ring-neutral-700 dark:text-neutral-400">
															(No disponible)
														</span>
													)}
												</div>

												{/* Product Details */}
												<div className="space-y-3 text-sm">
													{productType !== 'loan' && (
														<>
															<div>
																<span className="mb-1 block text-xs font-medium text-neutral-400">Rango de precio:</span>
																{!editMode ? (
																	price_range ? (
																		<span className="text-neutral-100 dark:text-neutral-200">
																			{formatCurrency(price_range.min, currency_code)} -{' '}
																			{formatCurrency(price_range.max, currency_code)}
																		</span>
																	) : (
																		<span className="italic text-neutral-500 dark:text-neutral-500">No configurado</span>
																	)
																) : (
																	<div className="flex gap-2">
																		<Input
																			placeholder="Min"
																			value={editConfig?.price_min || ''}
																			onChange={(value) => {
																				if (!editableData) return;
																				setEditableData({
																					...editableData,
																					product_configs: {
																						...editableData.product_configs,
																						[productType]: {
																							...editableData.product_configs[productType],
																							price_min: value
																						}
																					}
																				});
																			}}
																			allowedChars="numbers_only"
																			prefix={currency_code}
																			clearable={false}
																			containerClassName="flex-1"
																		/>
																		<Input
																			placeholder="Max"
																			value={editConfig?.price_max || ''}
																			onChange={(value) => {
																				if (!editableData) return;
																				setEditableData({
																					...editableData,
																					product_configs: {
																						...editableData.product_configs,
																						[productType]: {
																							...editableData.product_configs[productType],
																							price_max: value
																						}
																					}
																				});
																			}}
																			allowedChars="numbers_only"
																			prefix={currency_code}
																			clearable={false}
																			containerClassName="flex-1"
																		/>
																	</div>
																)}
															</div>

															<div>
																<span className="mb-1 block text-xs font-medium text-neutral-400">Inicial (%):</span>
																{!editMode ? (
																	down_payment ? (
																		<span className="text-neutral-100 dark:text-neutral-200">
																			{`${down_payment.min}%`} - {`${down_payment.max}%`}
																		</span>
																	) : (
																		<span className="italic text-neutral-500 dark:text-neutral-500">No configurado</span>
																	)
																) : (
																	<div className="flex gap-2">
																		<Input
																			placeholder="Min %"
																			value={editConfig?.down_payment_min || ''}
																			onChange={(value) => {
																				if (!editableData) return;
																				setEditableData({
																					...editableData,
																					product_configs: {
																						...editableData.product_configs,
																						[productType]: {
																							...editableData.product_configs[productType],
																							down_payment_min: value
																						}
																					}
																				});
																			}}
																			allowedChars="numbers_only"
																			clearable={false}
																			containerClassName="flex-1"
																		/>
																		<Input
																			placeholder="Max %"
																			value={editConfig?.down_payment_max || ''}
																			onChange={(value) => {
																				if (!editableData) return;
																				setEditableData({
																					...editableData,
																					product_configs: {
																						...editableData.product_configs,
																						[productType]: {
																							...editableData.product_configs[productType],
																							down_payment_max: value
																						}
																					}
																				});
																			}}
																			allowedChars="numbers_only"
																			clearable={false}
																			containerClassName="flex-1"
																		/>
																	</div>
																)}
															</div>
														</>
													)}

													{/* Payment Options */}
													<div>
														<span className="mb-1 block text-xs font-medium text-neutral-400">Planes de pago:</span>
														{!editMode ? (
															payment_options && payment_options.length > 0 ? (
																<div className="mt-1 space-y-1">
																	{payment_options.map((option, idx) => (
																		<div
																			key={idx}
																			className="flex items-center justify-between gap-2 text-neutral-100 dark:text-neutral-200"
																		>
																			<div className="flex items-center gap-2">
																				<CreditCard className="h-3 w-3 text-primary-400" />
																				<span>{option.installments} cuotas</span>
																			</div>
																			<span className="rounded-full bg-primary-200 px-2 py-1 text-xs font-medium text-primary-800 dark:bg-primary-900 dark:text-primary-300">
																				{(option.factor * 100 - 100) % 1 === 0
																					? (option.factor * 100 - 100).toFixed(0)
																					: (option.factor * 100 - 100).toFixed(1)}
																				% de interés
																			</span>
																		</div>
																	))}
																</div>
															) : (
																<span className="italic text-neutral-500 dark:text-neutral-500">No configurado</span>
															)
														) : (
															<div className="space-y-2">
																{editConfig?.payment_options.map((option, idx) => (
																	<div key={idx} className="flex items-center gap-1">
																		<Input
																			placeholder="Cuotas"
																			value={option.installments}
																			onChange={(value) => {
																				if (!editableData) return;
																				const newOptions = [...editConfig.payment_options];
																				newOptions[idx] = { ...newOptions[idx], installments: value };
																				setEditableData({
																					...editableData,
																					product_configs: {
																						...editableData.product_configs,
																						[productType]: {
																							...editableData.product_configs[productType],
																							payment_options: newOptions
																						}
																					}
																				});
																			}}
																			allowedChars="numbers_only"
																			clearable={false}
																			containerClassName="flex-1"
																		/>
																		<Input
																			placeholder="% interés"
																			value={option.factor}
																			onChange={(value) => {
																				if (!editableData || !LevelsConfig) return;

																				// Parse the new interest percentage
																				const newInterestPercent = parseFloat(value);
																				const installmentCount = parseFloat(option.installments);

																				if (!isNaN(newInterestPercent) && !isNaN(installmentCount)) {
																					// Check if this installment exists in base config
																					const countryConfig: LevelsConfig = LevelsConfig[country];
																					const baseFinancingOption = countryConfig?.base_financing_options?.find((opt) =>
																						opt.product_types?.includes(productType)
																					);
																					const baseOption = baseFinancingOption?.payment_options?.find(
																						(opt) => opt.installments === installmentCount
																					);

																					// If base option exists, check if new interest is higher
																					if (baseOption && baseOption.factor) {
																						const baseInterestPercent = (baseOption.factor - 1) * 100;
																						if (newInterestPercent > baseInterestPercent) {
																							setDialogMessage(
																								`No puedes establecer un interés mayor a ${baseInterestPercent.toFixed(2)}% para ${installmentCount} cuotas porque existe una regla base con ese valor que sobreescribirá este valor. La configuración base siempre prevalece con el interés más bajo.\n\nSi quieres aumentar este valor para todos los niveles, puedes hacerlo en la configuración base.`
																							);
																							return;
																						}
																					}
																				}

																				const newOptions = [...editConfig.payment_options];
																				newOptions[idx] = { ...newOptions[idx], factor: value };
																				setEditableData({
																					...editableData,
																					product_configs: {
																						...editableData.product_configs,
																						[productType]: {
																							...editableData.product_configs[productType],
																							payment_options: newOptions
																						}
																					}
																				});
																			}}
																			allowedChars="numeric"
																			clearable={false}
																			containerClassName="flex-1"
																		/>
																		<button
																			onClick={() => {
																				if (!editableData || !LevelsConfig) return;

																				const installmentCount = parseFloat(option.installments);
																				if (isNaN(installmentCount)) return;

																				const countryConfig: LevelsConfig = LevelsConfig[country];

																				// Check if it exists in base config
																				const baseFinancingOption = countryConfig?.base_financing_options?.find((opt) =>
																					opt.product_types?.includes(productType)
																				);
																				const isInBaseConfig = baseFinancingOption?.payment_options?.some(
																					(opt) => opt.installments === installmentCount
																				);

																				// If it exists in base config, prevent deletion (even if overridden)
																				if (isInBaseConfig) {
																					setDialogMessage(
																						'No se puede eliminar este plan de pago porque es una regla base que aplica a todos los niveles. Eliminarla afectaría a otros niveles. Solo puedes cambiar el interés.\n\nSi quieres eliminar esta opción para todos los niveles, puedes hacerlo en la configuración base.'
																					);
																					return;
																				}

																				// Allow deletion (only for level-specific payment options)
																				const newOptions = editConfig.payment_options.filter((_, i) => i !== idx);
																				setEditableData({
																					...editableData,
																					product_configs: {
																						...editableData.product_configs,
																						[productType]: {
																							...editableData.product_configs[productType],
																							payment_options: newOptions
																						}
																					}
																				});
																			}}
																			className={`${iconBtnBase} shrink-0 bg-red-600 p-1.5 text-white hover:bg-red-700`}
																			aria-label={`Eliminar plan ${option.installments} cuotas`}
																		>
																			<Trash2 className="h-3 w-3" />
																		</button>
																	</div>
																))}
																<button
																	onClick={() => {
																		if (!editableData) return;
																		const newOptions = [...(editConfig?.payment_options || []), { installments: '', factor: '' }];
																		setEditableData({
																			...editableData,
																			product_configs: {
																				...editableData.product_configs,
																				[productType]: {
																					...editableData.product_configs[productType],
																					payment_options: newOptions
																				}
																			}
																		});
																	}}
																	className={`${btnBase} bg-green-600 px-2 py-1 text-xs text-white hover:bg-green-700`}
																	aria-label={`Agregar plan para ${productType}`}
																>
																	<Plus className="h-3 w-3" />
																	Agregar plan
																</button>
															</div>
														)}
													</div>
												</div>
											</div>
										);
									}
								)}
							</div>
						</div>
					</div>
				</div>
			) : (
				<div id="panel-base" role="tabpanel" aria-labelledby="tab-base">
					{/* Base Configuration View */}
					<div className="space-y-6">
						{/* Edit Controls for Base */}
						<div className="flex justify-center gap-2">
							{!editMode ? (
								<button
									onClick={handleEnterBaseEditMode}
									className="flex select-none items-center gap-2 rounded-lg bg-primary-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-primary-700"
								>
									<Edit className="h-4 w-4" />
									Editar
								</button>
							) : (
								<>
									<button
										onClick={handleCancelEdit}
										disabled={saving}
										className="flex items-center gap-2 rounded-lg bg-neutral-700 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-neutral-600 disabled:opacity-50"
									>
										<X className="h-4 w-4" />
										Cancelar
									</button>
									<button
										onClick={handleSaveBase}
										disabled={saving}
										className="flex items-center gap-2 rounded-lg bg-green-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-green-700 disabled:opacity-50"
									>
										<Save className="h-4 w-4" />
										{saving ? 'Guardando...' : 'Guardar'}
									</button>
								</>
							)}
						</div>

						<div className="rounded-xl bg-foreground/70 p-6 dark:bg-neutral-950/50">
							<p className="mb-6 text-sm text-neutral-400">
								Esta configuración se aplica a todos los niveles como base. Los niveles individuales pueden sobrescribir estos
								valores.
							</p>

							{/* Base Price Ranges */}
							<div className="mb-8 space-y-4">
								<h3 className="text-lg font-semibold text-neutral-200">Rangos de Precio Base</h3>
								<div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
									{PROD_TYPES.filter((p) => p !== 'loan').map((productType) => {
										const Icon = productIcons[productType];
										const countryConfig: LevelsConfig = LevelsConfig[country];
										const basePriceRange = countryConfig?.base_price_ranges?.[productType];
										const editConfig = editableBaseData?.base_price_ranges[productType];

										return (
											<div
												key={productType}
												className="rounded-lg border border-neutral-800 bg-neutral-1000 p-4 dark:border-neutral-800 dark:bg-background"
											>
												<div className="mb-3 flex items-center gap-2">
													<Icon className="h-5 w-5 text-primary-500" />
													<h4 className={`font-semibold capitalize text-neutral-100`}>{productType}</h4>
												</div>
												{!editMode ? (
													<div className="space-y-2 text-sm">
														<div>
															<span className="text-neutral-300">Min: </span>
															<span className="text-neutral-100">{formatCurrency(basePriceRange?.min, currency_code)}</span>
														</div>
														<div>
															<span className="text-neutral-300">Max: </span>
															<span className="text-neutral-100">{formatCurrency(basePriceRange?.max, currency_code)}</span>
														</div>
														<div>
															<span className="text-neutral-300">Línea de crédito: </span>
															<span className="text-neutral-100">
																{basePriceRange?.credit_line ? creditLineLabel[basePriceRange.credit_line] : 'No especificada'}
															</span>
														</div>
													</div>
												) : (
													<div className="space-y-2">
														<Input
															placeholder="Min"
															value={editConfig?.min || ''}
															onChange={(value) => {
																if (!editableBaseData) return;
																setEditableBaseData({
																	...editableBaseData,
																	base_price_ranges: {
																		...editableBaseData.base_price_ranges,
																		[productType]: {
																			...editableBaseData.base_price_ranges[productType],
																			min: value
																		}
																	}
																});
															}}
															allowedChars="numbers_only"
															prefix={currency_code}
															clearable={false}
														/>
														<Input
															placeholder="Max"
															value={editConfig?.max || ''}
															onChange={(value) => {
																if (!editableBaseData) return;
																setEditableBaseData({
																	...editableBaseData,
																	base_price_ranges: {
																		...editableBaseData.base_price_ranges,
																		[productType]: {
																			...editableBaseData.base_price_ranges[productType],
																			max: value
																		}
																	}
																});
															}}
															allowedChars="numbers_only"
															prefix={currency_code}
															clearable={false}
														/>
														<div className="pt-1">
															<span className="mb-1 block text-xs font-medium text-neutral-400">Línea de crédito</span>
															<div className="flex flex-wrap gap-2">
																{[
																	{ value: '', label: 'No especificada' },
																	{ value: 'primary', label: creditLineLabel.primary },
																	{ value: 'secondary', label: creditLineLabel.secondary }
																].map((opt) => {
																	const selected = (editConfig?.credit_line ?? '') === opt.value;
																	return (
																		<button
																			key={opt.value || 'unset'}
																			onClick={() => {
																				if (!editableBaseData) return;
																				setEditableBaseData({
																					...editableBaseData,
																					base_price_ranges: {
																						...editableBaseData.base_price_ranges,
																						[productType]: {
																							...editableBaseData.base_price_ranges[productType],
																							credit_line: opt.value as '' | 'primary' | 'secondary'
																						}
																					}
																				});
																			}}
																			type="button"
																			className={`rounded px-2 py-1 text-xs transition-colors ${focusRing} ${
																				selected
																					? 'bg-primary-600 text-white'
																					: 'bg-neutral-800 text-neutral-300 hover:bg-neutral-700'
																			}`}
																		>
																			{opt.label}
																		</button>
																	);
																})}
															</div>
														</div>
													</div>
												)}
											</div>
										);
									})}
								</div>
							</div>

							{/* Base Financing Options */}
							<div className="space-y-4">
								<div className="flex items-center justify-between">
									<h3 className="text-lg font-semibold text-neutral-200">Opciones de Financiamiento Base</h3>
									{editMode && (
										<button
											onClick={() => {
												if (!editableBaseData) return;
												setEditableBaseData({
													...editableBaseData,
													base_financing_options: [
														...editableBaseData.base_financing_options,
														{
															product_types: [],
															down_payment_min: '40',
															down_payment_max: '50',
															payment_options: []
														}
													]
												});
											}}
											className={`${btnBase} bg-green-600 px-3 py-1.5 text-sm text-white hover:bg-green-700`}
											aria-label="Agregar regla base"
										>
											<Plus className="h-4 w-4" />
											Agregar Regla
										</button>
									)}
								</div>

								{(!editMode
									? (LevelsConfig[country] as LevelsConfig)?.base_financing_options
									: editableBaseData?.base_financing_options
								)?.map((option: any, idx: number) => {
									const editOpt = editMode ? editableBaseData?.base_financing_options[idx] : null;

									return (
										<div
											key={idx}
											className="rounded-xl bg-neutral-950/50 p-4 shadow-sm ring-1 ring-inset ring-neutral-800 dark:ring-neutral-700"
										>
											{editMode && (
												<div className="mb-3 flex justify-end">
													<button
														onClick={() => {
															if (!editableBaseData) return;
															setEditableBaseData({
																...editableBaseData,
																base_financing_options: editableBaseData.base_financing_options.filter((_, i) => i !== idx)
															});
														}}
														className={`${btnBase} bg-red-600 px-2 py-1 text-xs text-white hover:bg-red-700`}
														aria-label="Eliminar regla base"
													>
														<Trash2 className="h-3 w-3" />
														Eliminar Regla
													</button>
												</div>
											)}

											<div className="mb-3">
												<span className="text-sm font-medium text-neutral-400">Aplica a: </span>
												{!editMode ? (
													<span className="text-sm text-neutral-200">
														{sanitizeProductTypes(option.product_types).filter((p) => p !== 'loan').join(', ')}
													</span>
												) : (
													<div className="mt-2 flex flex-wrap gap-2">
														{PROD_TYPES.filter((pt) => pt !== 'loan').map((pt) => (
															<button
																key={pt}
																onClick={() => {
																	if (!editableBaseData || !editOpt) return;
																	const isSelected = editOpt.product_types.includes(pt);
																	const newProductTypes = isSelected
																		? editOpt.product_types.filter((p) => p !== pt)
																		: [...editOpt.product_types, pt];
																	const newOptions = [...editableBaseData.base_financing_options];
																	newOptions[idx] = { ...newOptions[idx], product_types: newProductTypes };
																	setEditableBaseData({ ...editableBaseData, base_financing_options: newOptions });
																}}
																className={`rounded px-2 py-1 text-xs transition-colors ${focusRing} ${
																	editOpt?.product_types.includes(pt)
																		? 'bg-primary-600 text-white'
																		: 'bg-neutral-800 text-neutral-300 hover:bg-neutral-700'
																}`}
															>
																{pt}
															</button>
														))}
													</div>
												)}
											</div>

											<div className="mb-3">
												<span className="text-sm font-medium text-neutral-400">Inicial (%): </span>
												{!editMode ? (
													<span className="text-sm text-neutral-200">
														{option.down_payment?.min}% - {option.down_payment?.max}%
													</span>
												) : (
													<div className="mt-2 flex gap-2">
														<Input
															placeholder="Min %"
															value={editOpt?.down_payment_min || ''}
															onChange={(value) => {
																if (!editableBaseData) return;
																const newOptions = [...editableBaseData.base_financing_options];
																newOptions[idx] = { ...newOptions[idx], down_payment_min: value };
																setEditableBaseData({ ...editableBaseData, base_financing_options: newOptions });
															}}
															allowedChars="numbers_only"
															clearable={false}
															containerClassName="flex-1"
														/>
														<Input
															placeholder="Max %"
															value={editOpt?.down_payment_max || ''}
															onChange={(value) => {
																if (!editableBaseData) return;
																const newOptions = [...editableBaseData.base_financing_options];
																newOptions[idx] = { ...newOptions[idx], down_payment_max: value };
																setEditableBaseData({ ...editableBaseData, base_financing_options: newOptions });
															}}
															allowedChars="numbers_only"
															clearable={false}
															containerClassName="flex-1"
														/>
													</div>
												)}
											</div>

											<div>
												<div className="flex items-center justify-between">
													<span className="text-sm font-medium text-neutral-400">Planes de pago:</span>
													{editMode && (
														<button
															onClick={() => {
																if (!editableBaseData || !editOpt) return;
																const newOptions = [...editableBaseData.base_financing_options];
																newOptions[idx] = {
																	...newOptions[idx],
																	payment_options: [...editOpt.payment_options, { installments: '', factor: '' }]
																};
																setEditableBaseData({ ...editableBaseData, base_financing_options: newOptions });
															}}
															className={`${btnBase} bg-green-600 px-2 py-1 text-xs text-white hover:bg-green-700`}
															aria-label="Agregar plan base"
														>
															<Plus className="h-3 w-3" />
															Agregar Plan
														</button>
													)}
												</div>
												{!editMode ? (
													<div className="mt-2 space-y-1">
														{option.payment_options
															?.sort((a: any, b: any) => a.installments - b.installments)
															.map((payOpt: any, payIdx: number) => (
																<div key={payIdx} className="flex items-center justify-between text-sm text-neutral-200">
																	<span>{payOpt.installments} cuotas</span>
																	<span className="rounded-full bg-primary-200 px-2 py-1 text-xs font-medium text-primary-800 dark:bg-primary-900 dark:text-primary-300">
																		{((payOpt.factor - 1) * 100).toFixed(2)}% de interés
																	</span>
																</div>
															))}
													</div>
												) : (
													<div className="mt-2 space-y-2">
														{editOpt?.payment_options.map((payOpt: any, payIdx: number) => (
															<div key={payIdx} className="flex items-center gap-1">
																<Input
																	placeholder="Cuotas"
																	value={payOpt.installments}
																	onChange={(value) => {
																		if (!editableBaseData || !editOpt) return;
																		const newPayOpts = [...editOpt.payment_options];
																		newPayOpts[payIdx] = { ...newPayOpts[payIdx], installments: value };
																		const newOptions = [...editableBaseData.base_financing_options];
																		newOptions[idx] = { ...newOptions[idx], payment_options: newPayOpts };
																		setEditableBaseData({ ...editableBaseData, base_financing_options: newOptions });
																	}}
																	allowedChars="numbers_only"
																	clearable={false}
																	containerClassName="flex-1"
																/>
																<Input
																	placeholder="% interés"
																	value={payOpt.factor}
																	onChange={(value) => {
																		if (!editableBaseData || !editOpt) return;
																		const newPayOpts = [...editOpt.payment_options];
																		newPayOpts[payIdx] = { ...newPayOpts[payIdx], factor: value };
																		const newOptions = [...editableBaseData.base_financing_options];
																		newOptions[idx] = { ...newOptions[idx], payment_options: newPayOpts };
																		setEditableBaseData({ ...editableBaseData, base_financing_options: newOptions });
																	}}
																	allowedChars="numeric"
																	clearable={false}
																	containerClassName="flex-1"
																/>
																<button
																	onClick={() => {
																		if (!editableBaseData || !editOpt) return;
																		const newPayOpts = editOpt.payment_options.filter((_, i) => i !== payIdx);
																		const newOptions = [...editableBaseData.base_financing_options];
																		newOptions[idx] = { ...newOptions[idx], payment_options: newPayOpts };
																		setEditableBaseData({ ...editableBaseData, base_financing_options: newOptions });
																	}}
																	className={`${iconBtnBase} shrink-0 bg-red-600 p-1.5 text-white hover:bg-red-700`}
																	aria-label={`Eliminar plan base ${payOpt.installments} cuotas`}
																>
																	<Trash2 className="h-3 w-3" />
																</button>
															</div>
														))}
													</div>
												)}
											</div>
										</div>
									);
								})}
							</div>
						</div>
					</div>
				</div>
			)}

			{/* Dialog for alerts */}
			{dialogMessage && (
				<Dialog title="" onClose={() => setDialogMessage(null)}>
					<p className="whitespace-pre-wrap">{dialogMessage}</p>
				</Dialog>
			)}
		</div>
	);
}
